/**
 * @engine/core - LevelDependencies
 *
 * Shared helper to extract the DIRECT asset-GUID references from a serialized level
 * (or GameObject). Asset fields are derived from each component's `@field({ type: 'asset' })`
 * metadata — no hardcoded map. Add an asset @field to a component and it is picked up here.
 *
 * Used by:
 *  - the build's `AnalyzeDependenciesStep` (then expanded transitively via VFS/.cache),
 *  - the editor's live `LevelDependencyProvider` (expanded via FineIndex),
 *  - the player's runtime fallback scan (over the deserialized level).
 *
 * Transitive closure (vfx→texture, atlas→source, prefab→refs, material→shader/texture) is
 * host-specific and lives in each caller — this helper returns ONLY direct refs.
 */

import { getAllComponents } from "./attributes";
import type { SerializedGameObject, SerializedLevel } from "./LevelSerializer";

let _assetRefFields: Record<string, Array<{ field: string; assetType: string }>> | null = null;

/**
 * Map componentTypeId → its asset-reference fields, derived from `@field({ type: 'asset', assetType })`.
 * Memoized (component metadata is registered once per session); call {@link resetAssetReferenceFields}
 * after dynamic (re)registration of components.
 */
export function getAssetReferenceFields(): Record<string, Array<{ field: string; assetType: string }>> {
  if (_assetRefFields) return _assetRefFields;
  const out: Record<string, Array<{ field: string; assetType: string }>> = {};
  for (const [typeId, meta] of getAllComponents()) {
    const refs: Array<{ field: string; assetType: string }> = [];
    for (const f of meta.fields.values()) {
      if (f.type === "asset") refs.push({ field: f.propertyKey, assetType: f.assetType ?? "unknown" });
    }
    if (refs.length > 0) out[typeId] = refs;
  }
  return (_assetRefFields = out);
}

/** Drop the memoized asset-field map (after dynamic (re)registration of components). */
export function resetAssetReferenceFields(): void {
  _assetRefFields = null;
}

/** Recursively collect asset-GUID refs from a serialized GameObject (and its children) into `out`. */
export function extractAssetRefsFromSerializedGO(go: SerializedGameObject, out: Set<string>): void {
  const refFields = getAssetReferenceFields();
  if (go.components) {
    for (const comp of go.components) {
      const refs = refFields[comp.typeId];
      if (!refs) continue;
      for (const ref of refs) {
        const value = comp.values[ref.field];
        if (typeof value === "string" && value.length > 0) out.add(value);
      }
    }
  }
  if (go.children) {
    for (const child of go.children) extractAssetRefsFromSerializedGO(child, out);
  }
}

/** Collect all DIRECT asset-GUID refs from a serialized level (no transitive expansion). */
export function extractAssetRefsFromSerializedLevel(level: SerializedLevel): Set<string> {
  const out = new Set<string>();
  for (const go of level.rootObjects) extractAssetRefsFromSerializedGO(go, out);
  return out;
}
